import React, { FC } from 'react'

//* utils
import { ModuleNames } from '../../../../constants/componentNames.constants'
import { TYPE } from '../../../../constants/partners'
import { UIPATH } from '../../../../constants/urls'
import MarketoFormContainer from '../../../global/marketo/style'

//* components
import ContentBlock from '../../../global/contentBlock'
import ContentColumns from '../../../global/contentColumns'
import Divider from '../../../global/divider'
import FeaturedResource from '../../../global/featuredResource'
import Grid from '../../../global/grid'
import Hero from '../../../global/hero'
import Icons from '../../../global/icons'
import Image from '../../../global/image'
import KeyStats from '../../../global/keyStats'
import MainCTA from '../../../global/mainCta'
import Marketo from '../../../global/marketo'
import MediaText from '../../../global/mediaText'
import ParagraphBlock from '../../../global/paragraphBlock'
import PlatformDiagram from '../../../global/platformDiagram'
import Quote from '../../../global/quote'
import ResourceRow from '../../../global/resourceRow'
import ResourceTabs from '../../../global/resourceTabs'
import Slider from '../../../global/slider'
import Table from '../../../global/table'
import Tiles from '../../../global/tiles'
import VideoPlayer from '../../../global/videoPlayer'
import VideoSpotlight from '../../../global/videoSpotlight'

type Props = {
  pageData: any
  pageLocale: string
  location: any
}

export const Modules: FC<Props> = ({ pageData = [], pageLocale, location }) => {
  const metaInformation = pageData.metaInformation || []
  let thankYouPageUrl: string = '/'

  for (let info of metaInformation) {
    if (info.__typename === ModuleNames.TOPIC_PARTNER_DETAILS && info.partnerType) {
      if (info.partnerType === TYPE.BUSINESS) {
        thankYouPageUrl = `${UIPATH.PROD}/g/thank-you-business-partner`
      } else if (info.partnerType === TYPE.TECHNOLOGY) {
        thankYouPageUrl = `${UIPATH.PROD}/g/thank-you-contact`
      }
    }
  }

  const formSuccessHandler = (form: any) => {
    if (window && window.hasOwnProperty('MktoForms2') && form) {
      form.onSuccess(() => {
        window.location.href = thankYouPageUrl
        return false
      })
    }
  }

  return pageData.flexSection?.map((module: any, i: number) => {
    switch (module.__typename) {
      case ModuleNames.MEDIA_TEXT:
        return <MediaText data={module} key={`page-component--${i}`} />

      case ModuleNames.ANCHOR_LINK:
        return <span id={module?.identifier || ''} key={`page-component--${i}`}></span>

      case ModuleNames.GLOBAL_CONTENT_COLUMNS:
        return <ContentColumns data={module} key={`page-component--${i}`} />

      case ModuleNames.GLOBAL_CONTENT_BLOCK:
        return <ContentBlock data={module} key={`page-component--${i}`} />

      case ModuleNames.ICONS:
        return <Icons data={module} key={`page-component--${i}`} />

      case ModuleNames.GRID:
        return <Grid data={module} key={`page-component--${i}`} />

      case ModuleNames.HERO:
        const pageTitle = pageData?.pageName || pageData?.name

        return (
          <Hero
            data={module}
            location={location}
            pageTitle={pageTitle}
            showBreadcrumbs={true}
            key={`page-component--${i}`}
            pageType={TYPE.PARTNERS}
            metaInformation={pageData.metaInformation}
            pageUseCase={pageData.pageUseCase}
            locale={pageLocale}
            breadcrumb={pageData.breadcrumb}
          />
        )

      case ModuleNames.GLOBAL_MAIN_CTA:
        return <MainCTA data={module} locale={pageLocale} key={`page-component--${i}`} />

      case ModuleNames.TILES:
        return <Tiles data={module} key={`page-component--${i}`} />

      case ModuleNames.MARKETO_FORM:
        return (
          <MarketoFormContainer key={`page-component--${i}`}>
            <div>
              {module.title && <p className="FormTitle">{module.title}</p>}
              {module.subtitle && <p className="FormSubtitle">{module.subtitle}</p>}
              <Marketo formData={module.marketoForm} formCallbacks={formSuccessHandler} />
              {module.marketoFinePrint && (
                <p className="FormFinePrint">{module.marketoFinePrint}</p>
              )}
            </div>
          </MarketoFormContainer>
        )

      case ModuleNames.DIVIDER:
        return <Divider data={module} key={`page-component--${i}`} />

      case ModuleNames.GLOBAL_KEY_STATS:
        return <KeyStats data={module} key={`page-component--${i}`} />

      case ModuleNames.VIDEO_SPOTLIGHT:
        return <VideoSpotlight data={module} key={`page-component--${i}`} />

      case ModuleNames.RESOURCE_ROW:
        return (
          <ResourceRow
            data={module}
            pageType={TYPE.PARTNERS}
            key={`page-component--${i}`}
            locale={pageLocale}
          />
        )

      case ModuleNames.ICONS:
        return <Icons data={module} key={`page-component--${i}`} />

      case ModuleNames.GRID:
        return <Grid data={module} key={`page-component--${i}`} />

      case ModuleNames.QUOTE:
        return <Quote data={module} key={`page-component--${i}`} />

      case ModuleNames.FEATURED_RESOURCE:
        return (
          <FeaturedResource
            data={module}
            pageLocale={pageLocale}
            pageType={TYPE.PARTNERS}
            key={`page-component--${i}`}
          />
        )

      case ModuleNames.SLIDER:
        return <Slider data={module} key={`page-component--${i}`} />

      case ModuleNames.TABLE:
        return <Table data={module} key={`page-component--${i}`} />

      case ModuleNames.RESOURCE_TABS:
        return <ResourceTabs data={module} key={`page-component--${i}`} />

      case ModuleNames.PLATFORM_DIAGRAM:
        return <PlatformDiagram data={module} key={`page-component--${i}`} />

      case ModuleNames.TILES:
        return <Tiles data={module} key={`page-component--${i}`} />

      case ModuleNames.WRAPPER_ASSET:
        const imageData = {
          imageFile: {
            file: {
              title: module.altText,
              url: module.image?.file?.url,
            },
          },
        }

        return <Image data={imageData} key={`page-component--${i}`} outerPage={TYPE.PARTNERS} />

      case ModuleNames.WRAPPER_VIDEO:
        const imgSrc = module.image?.file?.url || ''
        const bgAlt = module.altText || ''
        const start = module.start || 0
        let videoId, type, videoUrl

        if (module.youtubeId) {
          videoId = module.youtubeId
          type = 'youtube'
        } else if (module.vimeoId) {
          videoId = module.vimeoId
          type = 'vimeo'
        } else if (module.videoUrl) {
          videoUrl = module.videoUrl
          type = 'html-video'
        } else if (module.tencentVideoUrl) {
          videoUrl = module.tencentVideoUrl
          type = 'tencent-video'
        }

        if (videoId || videoUrl) {
          return (
            <VideoPlayer
              videoId={videoId}
              videoUrl={videoUrl}
              width={'100%'}
              height={'429'}
              img={imgSrc}
              type={type}
              start={start}
              backgroundAltText={bgAlt}
              key={`page-component--${i}`}
              outerPage={TYPE.PARTNERS}
            />
          )
        } else {
          return null
        }

      case ModuleNames.PARAGRAPH_BLOCK:
        return <ParagraphBlock data={module} key={`page-component--${i}`} />
    }
  })
}
